/**
 * Client-side PII redactor for CloudWatch RUM telemetry (#3019).
 *
 * This is a STANDALONE reimplementation of the masking behaviour in
 * `shared/utils/src/redact.ts` + the `PII_LOG_RULES` in
 * `shared/utils/src/logger.ts`. The SPA bundle cannot import
 * `@policy-tracker/utils` — that package pulls in the AWS SDK + DynamoDB client
 * transitively, which must never reach the browser. Same precedent as
 * `src/lib/cognito-domain.ts`. Drift from the shared rules is caught by
 * `src/lib/rum-redact.test.ts`, which compares this output against the shared
 * `redact`/`PII_LOG_RULES` by path.
 *
 * Two deliberate differences from the shared `redact()`:
 *  1. NO `NODE_ENV === 'test'` bypass — telemetry must be redacted in every
 *     environment, including tests. (The shared redactor short-circuits in
 *     tests; copying that would let unredacted PII reach RUM under any build
 *     whose NODE_ENV happened to be 'test'.)
 *  2. Request/response bodies are stripped ENTIRELY. The RUM contract never
 *     includes a request or response body — masking field-by-field is not
 *     enough because bodies carry free-form PII under arbitrary keys. Any key
 *     named `body`, `requestBody`, or `responseBody` (case-insensitive) is
 *     dropped at every nesting level before masking runs.
 */

/** Mask strategies — mirrors `MaskType` in `shared/utils/src/redact.ts`. */
type MaskType = 'all' | 'email';

interface MaskRule {
    /** Glob-style wildcard pattern for field names. Case-insensitive. */
    pattern: string;
    /** How to mask matched field values. */
    type: MaskType;
}

/**
 * Masking rules mirroring `PII_LOG_RULES` from `shared/utils/src/logger.ts`.
 * The `lastFour` strategy is omitted because `PII_LOG_RULES` does not use it.
 */
const MASK_RULES: MaskRule[] = [
    { pattern: '*email*', type: 'email' },
    { pattern: 'firstName', type: 'all' },
    { pattern: 'lastName', type: 'all' },
    { pattern: '*token*', type: 'all' },
    { pattern: '*password*', type: 'all' },
    { pattern: '*secret*', type: 'all' },
];

/** Allow list — exact, case-insensitive field names that bypass masking. */
const ALLOW: string[] = ['emailType'];

/**
 * Field names (case-insensitive) whose values are request/response bodies and
 * must never appear in telemetry. These keys are dropped entirely, at any depth.
 */
const BODY_KEYS = new Set(['body', 'requestbody', 'responsebody']);

const MAX_DEPTH = 50;

const compiledPatterns = new Map<string, RegExp>();
for (const rule of MASK_RULES) {
    const p = rule.pattern.toLowerCase();
    if (p !== '*' && !compiledPatterns.has(p)) {
        const escaped = p.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
        compiledPatterns.set(p, new RegExp(`^${escaped}$`));
    }
}

/** Case-insensitive glob match using the pre-compiled pattern map. */
function globMatch(pattern: string, fieldName: string): boolean {
    const p = pattern.toLowerCase();
    const f = fieldName.toLowerCase();
    if (p === '*') {
        return true;
    }
    const regex = compiledPatterns.get(p);
    if (regex) {
        return regex.test(f);
    }
    const escaped = p.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
    return new RegExp(`^${escaped}$`).test(f);
}

/** Applies a mask to a single field value according to the mask type. */
function applyMask(value: unknown, type: MaskType): unknown {
    if (type === 'all') {
        return '[REDACTED]';
    }
    // type === 'email'
    if (typeof value !== 'string') {
        return '[REDACTED]';
    }
    const atIndex = value.indexOf('@');
    if (atIndex < 0) {
        return '[REDACTED]';
    }
    const local = value.slice(0, atIndex);
    const domain = value.slice(atIndex);
    if (local.length <= 1) {
        return `${local}***${domain}`;
    }
    return `${local[0]}***${domain}`;
}

interface Ctx {
    seen: Set<object>;
}

function redactValue(data: unknown, ctx: Ctx, depth: number): unknown {
    if (Array.isArray(data)) {
        if (depth >= MAX_DEPTH) {
            return '[Max Depth]';
        }
        if (ctx.seen.has(data)) {
            return '[Circular]';
        }
        ctx.seen.add(data);
        const result = data.map((item) => redactValue(item, ctx, depth + 1));
        ctx.seen.delete(data);
        return result;
    }

    if (data !== null && typeof data === 'object') {
        if (depth >= MAX_DEPTH) {
            return '[Max Depth]';
        }

        if (data instanceof Date) {
            return data.toISOString();
        }
        if (data instanceof Map) {
            return '[Map]';
        }
        if (data instanceof Set) {
            return '[Set]';
        }
        if (data instanceof RegExp) {
            return data.toString();
        }
        if (data instanceof Error) {
            if (ctx.seen.has(data)) {
                return '[Circular]';
            }
            ctx.seen.add(data);
            const errObj: Record<string, unknown> = {
                name: data.name,
                message: data.message,
                stack: data.stack,
            };
            for (const key of Object.keys(data)) {
                errObj[key] = (data as unknown as Record<string, unknown>)[key];
            }
            const result = redactValue(errObj, ctx, depth + 1);
            ctx.seen.delete(data);
            return result;
        }

        if (ctx.seen.has(data as object)) {
            return '[Circular]';
        }
        ctx.seen.add(data as object);
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            // Drop request/response bodies entirely — never goes to telemetry.
            if (BODY_KEYS.has(key.toLowerCase())) {
                continue;
            }
            result[key] = redactField(key, value, ctx, depth + 1);
        }
        ctx.seen.delete(data as object);
        return result;
    }

    return data;
}

function redactField(key: string, value: unknown, ctx: Ctx, depth: number): unknown {
    const keyLower = key.toLowerCase();
    if (ALLOW.some((a) => a.toLowerCase() === keyLower)) {
        return value;
    }
    for (const rule of MASK_RULES) {
        if (globMatch(rule.pattern, key)) {
            return applyMask(value, rule.type);
        }
    }
    return redactValue(value, ctx, depth);
}

/**
 * Redacts PII from a value before it is attached to RUM telemetry.
 *
 * Mirrors the masking of `shared/utils/src/redact.ts` with `PII_LOG_RULES`,
 * then additionally strips any request/response body keys. Unlike the shared
 * redactor, this ALWAYS redacts — there is no test-environment bypass.
 *
 * @param data - The value to redact (any type; objects/arrays are recursed).
 * @returns A redacted copy safe to send to RUM.
 */
export function redactForRum(data: unknown): unknown {
    return redactValue(data, { seen: new Set() }, 0);
}
